"use client";

import { TarotRequestCreate } from "@/types/tarot";
import { SimplePhoneInput } from "@/components/SimplePhoneInput";
import { PhoneValidationResult } from "@/hooks/usePhoneValidation";
import { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { getMarketPhoneCountry, isPortugueseLanguage } from "@/config/app";
import { detectLikelyEmailDomainTypo } from "@/lib/emailDomainSuggestion";
import { parsePhoneNumber } from "libphonenumber-js";

/**
 * Perfil de telefone do mercado: máscara local, DDI e quantidade de dígitos.
 * O seletor de país do input continua disponível; isto define apenas o padrão.
 */
const PHONE_PROFILES = {
  BR: {
    dialCode: "55",
    localDigits: 11,
    placeholder: "(11) 99999-9999",
    format: (digits: string): string => {
      if (digits.length < 3) return `(${digits}`;
      if (digits.length < 8) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    },
  },
  PT: {
    dialCode: "351",
    localDigits: 9,
    placeholder: "912 345 678",
    format: (digits: string): string => {
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    },
  },
} as const;

interface Step1ContactProps {
  formData: TarotRequestCreate;
  fieldErrors: Set<string>;
  onInputChange: (field: string, value: string) => void;
  onFieldErrorRemove: (field: string) => void;
  emailDomainBlocked?: boolean;
  emailDomainSuggestionReviewedFor?: string | null;
  onEmailDomainSuggestionReviewed?: (email: string) => void;
}

export function Step1Contact({
  formData,
  fieldErrors,
  onInputChange,
  onFieldErrorRemove,
  emailDomainBlocked = false,
  emailDomainSuggestionReviewedFor,
  onEmailDomainSuggestionReviewed,
}: Step1ContactProps) {
  const { t } = useI18n();
  const [emailRealtimeValid, setEmailRealtimeValid] = useState<boolean | null>(
    null,
  );

  const phoneCountry = getMarketPhoneCountry();
  const phoneProfile =
    phoneCountry === "PT" ? PHONE_PROFILES.PT : PHONE_PROFILES.BR;

  const toLocalDigits = (
    input: string,
    profile: (typeof PHONE_PROFILES)[keyof typeof PHONE_PROFILES] = phoneProfile,
  ): string => {
    const rawDigits = input.replace(/\D/g, "");
    return rawDigits.length > profile.localDigits
      ? rawDigits.slice(-profile.localDigits)
      : rawDigits.slice(0, profile.localDigits);
  };

  const formatWhatsAppNumber = (
    input: string,
    profile: (typeof PHONE_PROFILES)[keyof typeof PHONE_PROFILES] = phoneProfile,
  ): string => {
    const digits = toLocalDigits(input, profile);
    if (digits.length === 0) return "";
    return profile.format(digits);
  };

  const getDisplayPhoneFromStoredValue = (storedPhone: string): string => {
    if (storedPhone.trim().startsWith("+")) {
      try {
        return parsePhoneNumber(storedPhone).formatNational();
      } catch {
        // Mantém a edição possível caso um valor internacional esteja incompleto.
      }
    }
    return formatWhatsAppNumber(storedPhone);
  };

  const handlePhoneChange = (
    value: string,
    validation: PhoneValidationResult,
  ) => {
    // Persistir E.164 faz a validação final respeitar o DDI escolhido, mesmo
    // quando ele é diferente do mercado da página (por exemplo, +351 no Brasil).
    onInputChange(
      "querent_phone",
      validation.isValid && validation.internationalNumber
        ? `+${validation.internationalNumber.replace(/\D/g, "")}`
        : value,
    );
    if (fieldErrors.has("querent_phone")) {
      onFieldErrorRemove("querent_phone");
    }
  };

  const isWhatsAppAvailable = isPortugueseLanguage();
  const deliveryOption =
    formData.delivery_option ??
    (isWhatsAppAvailable ? "whatsapp" : "email");
  const wantsEmail = deliveryOption === "email" || deliveryOption === "email_whatsapp";
  const wantsWhatsApp = deliveryOption === "whatsapp" || deliveryOption === "email_whatsapp";
  const normalizedCurrentEmail = (formData.querent_email || "").trim().toLowerCase();
  const emailDomainSuggestion = detectLikelyEmailDomainTypo(normalizedCurrentEmail);
  const emailSuggestionPending = Boolean(
    emailDomainSuggestion &&
      emailDomainSuggestionReviewedFor !== normalizedCurrentEmail,
  );

  return (
    <div className="space-y-3">
      {isWhatsAppAvailable ? (
        <div className={`bg-white/50 rounded-lg p-3 border mb-3 ${fieldErrors.has("delivery_option") ? "border-red-500" : "border-[rgba(232,115,115,0.2)]"}`}>
          <div className="flex items-start space-x-2">
            <div className="text-xl">📬</div>
            <div className="space-y-2">
              <p className="form-description-text">
                <span className="font-title">
                  {t(
                    "step1.delivery.choose",
                    "Como você prefere receber sua carta?",
                  )}
                </span>
              </p>
              <div className="space-y-1">
                <label className="flex items-start space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={wantsEmail}
                    onChange={() => {
                      const next = wantsEmail
                        ? (wantsWhatsApp ? "whatsapp" : "")
                        : (wantsWhatsApp ? "email_whatsapp" : "email");
                      onInputChange("delivery_option", next);
                      if (next && fieldErrors.has("delivery_option")) onFieldErrorRemove("delivery_option");
                      if (wantsEmail) {
                        setEmailRealtimeValid(null);
                      }
                    }}
                  />
                  <span className="form-description-text">
                    <span className="font-title">
                      {t("step1.delivery.email_channel", "Email")}
                    </span>{" "}
                    —{" "}
                    {t(
                      "step1.delivery.email_only_desc",
                      "Receba a carta por email.",
                    )}
                  </span>
                </label>
                <label className="flex items-start space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={wantsWhatsApp}
                    onChange={() => {
                      const next = wantsWhatsApp
                        ? (wantsEmail ? "email" : "")
                        : (wantsEmail ? "email_whatsapp" : "whatsapp");
                      onInputChange("delivery_option", next);
                      if (next && fieldErrors.has("delivery_option")) onFieldErrorRemove("delivery_option");
                      if (wantsWhatsApp && fieldErrors.has("querent_phone")) onFieldErrorRemove("querent_phone");
                    }}
                  />
                  <span className="form-description-text">
                    <span className="font-title">
                      {t("step1.delivery.whatsapp_channel", "WhatsApp")}
                    </span>{" "}
                    —{" "}
                    {t(
                      "step1.delivery.email_whatsapp_desc",
                      "Receba a carta pelo WhatsApp.",
                    )}
                  </span>
                </label>
              </div>
              {fieldErrors.has("delivery_option") && (
                <p className="text-red-600 text-xs" role="alert">
                  {t("step1.validation.delivery_required", "Selecione ao menos um canal de entrega.")}
                </p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white/50 rounded-lg p-3 border border-[rgba(232,115,115,0.2)] mb-3">
          <div className="flex items-start space-x-2">
            <div className="text-xl">📬</div>
            <div>
              <p className="form-description-text">
                <span className="font-title">
                  {t("step1.delivery.title", "Entrega da Carta:")}
                </span>{" "}
                {t(
                  "step1.delivery.email_delivery",
                  "Sua carta será entregue por email.",
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {wantsWhatsApp && isWhatsAppAvailable && (
        <SimplePhoneInput
          label={t("step1.whatsapp_label", "Número do WhatsApp")}
          value={getDisplayPhoneFromStoredValue(formData.querent_phone || "")}
          onChange={handlePhoneChange}
          hasError={fieldErrors.has("querent_phone")}
          required={true}
          defaultCountry={phoneCountry}
          placeholder={
            phoneCountry === "BR"
              ? t("step1.whatsapp_placeholder", phoneProfile.placeholder)
              : phoneProfile.placeholder
          }
        />
      )}

      {deliveryOption === "whatsapp" && (
        <p className="text-xs text-slate-600">
          {t("step1.delivery.payment_email_note", "O email do pagador poderá ser solicitado na etapa de pagamento. Sua carta será entregue somente pelo WhatsApp.")}
        </p>
      )}

      {wantsEmail && <div className="mt-3">
        <label className="form-label-compact">
          {t("step1.email_label", "Email")}{" "}
          <span className="text-red-600">*</span>
        </label>
        <input
          type="email"
          value={formData.querent_email || ""}
          onChange={(e) => {
            const value = e.target.value;
            onInputChange("querent_email", value);
            if (fieldErrors.has("querent_email"))
              onFieldErrorRemove("querent_email");
            if (value.trim()) {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              setEmailRealtimeValid(emailRegex.test(value.trim()));
            } else {
              setEmailRealtimeValid(null);
            }
          }}
          placeholder={t("step1.email_placeholder", "seu.email@exemplo.com")}
          className={`w-full rounded-md border px-3 py-2 text-sm bg-white/80 focus:outline-none focus:ring-2 focus:ring-[rgba(232,115,115,0.4)] transition ${
            fieldErrors.has("querent_email")
              ? "border-red-500"
              : emailRealtimeValid === false
                ? "border-orange-500"
                : emailRealtimeValid === true
                  ? "border-green-500"
                  : "border-[rgba(232,115,115,0.3)]"
          }`}
          autoComplete="email"
          inputMode="email"
          required
          aria-required={true}
          aria-invalid={fieldErrors.has("querent_email")}
        />
        {emailSuggestionPending && emailDomainSuggestion && (
          <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3">
            <p className="text-xs font-semibold text-amber-900">
              {t("step1.email_domain_suggestion.title", "Confirme seu email")}
            </p>
            <p className="mt-1 text-xs text-amber-800">
              {t("step1.email_domain_suggestion.prefix", "Você quis dizer")}{" "}
              <span className="font-semibold">{emailDomainSuggestion.suggestedEmail}</span>?
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700"
                onClick={() => {
                  onInputChange(
                    "querent_email",
                    emailDomainSuggestion.suggestedEmail,
                  );
                  onEmailDomainSuggestionReviewed?.(
                    emailDomainSuggestion.suggestedEmail,
                  );
                  if (fieldErrors.has("querent_email")) {
                    onFieldErrorRemove("querent_email");
                  }
                }}
              >
                {t(
                  "step1.email_domain_suggestion.use",
                  "Usar email sugerido",
                )}
              </button>
              <button
                type="button"
                className="rounded-md border border-amber-400 bg-white px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100"
                onClick={() => {
                  onEmailDomainSuggestionReviewed?.(normalizedCurrentEmail);
                  if (fieldErrors.has("querent_email")) {
                    onFieldErrorRemove("querent_email");
                  }
                }}
              >
                {t(
                  "step1.email_domain_suggestion.keep",
                  "Manter email digitado",
                )}
              </button>
            </div>
          </div>
        )}
        {fieldErrors.has("querent_email") && (
          <p className="text-red-600 text-xs mt-1">
            {emailDomainBlocked
              ? t(
                  "step1.email_domain_blocked",
                  "Este domínio de email não existe. Revise o endereço para continuar.",
                )
              : t("step1.email_invalid", "Informe um email válido.")}
          </p>
        )}
        {!fieldErrors.has("querent_email") &&
          emailRealtimeValid === false &&
          formData.querent_email && (
            <p className="text-orange-600 text-xs mt-1 flex items-center">
              <svg
                className="mr-1 h-3.5 w-3.5 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v4m0 4h.01M10.29 3.86l-7.17 12.42A2 2 0 0 0 4.83 19h14.34a2 2 0 0 0 1.71-2.72L13.71 3.86a2 2 0 0 0-3.42 0Z"
                />
              </svg>
              {t(
                "step1.email_format_warning",
                "Verifique o formato do email (ex: nome@exemplo.com)",
              )}
            </p>
          )}
        {!fieldErrors.has("querent_email") && emailRealtimeValid === true && (
          <p className="text-green-600 text-xs mt-1 flex items-center">
            <svg
              className="mr-1 h-3.5 w-3.5 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20 6 9 17l-5-5"
              />
            </svg>
            {t("step1.email_valid", "Email válido")}
          </p>
        )}
      </div>}
    </div>
  );
}
